<template>
  <div
    class="bg-background-neutral-secondary rounded-2xl border"
    :class="[
      isInvalid && !isSingleBlock
        ? 'border-background-negative-primary'
        : 'border-white dark:border-transparent',
      !isSingleBlock && 'p-4',
    ]"
  >
    <div class="flex items-center justify-between space-x-2">
      <div class="w-1/2">
        <slot name="passengerData" :passenger-text="passengerText">
          <p class="text-text-neutral-primary truncate font-semibold">
            {{ passengerText?.title }}
          </p>
          <span class="block truncate text-sm">
            {{ passengerText?.description }}
          </span>
        </slot>
      </div>
      <FreeButton
        :data-qa-id="
          hasSelectedPassenger
            ? `${sourcePage}.passengerCard.${paxType}.edit`
            : `${sourcePage}.passengerCard.${paxType}.add`
        "
        size="sm"
        type="button"
        class="passenger-action-button w-32 shrink-0"
        :class="
          hasSelectedPassenger
            ? 'bg-situational-lightgrey text-text-neutral-primary'
            : 'green-main-button'
        "
        @click="$emit('onActionButtonClick')"
      >
        {{ hasSelectedPassenger ? t('passenger.edit') : t('passenger.add') }}
      </FreeButton>
    </div>
    <slot name="bottomExtra" />
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import type { PassengerKey, Passenger } from '@aviata/booking-data';

  import messages from '../locales.json';
  import { FreeButton } from '../../../shared/ui';
  import { bookingPassengerLib } from '../lib';

  const props = defineProps<{
    sourcePage?: string;
    paxType?: string;
    isInvalid: boolean;
    passenger?: Passenger | null;
    isSingleBlock?: boolean;
    placeholderData?: {
      key: PassengerKey;
      ageBetween: {
        min: number;
        max?: number;
      };
    };
  }>();

  defineEmits<{
    onActionButtonClick: [];
  }>();

  const { t } = useI18n({ messages });

  const passengerTypePlaceholder = computed(() => {
    if (!props.placeholderData) {
      return;
    }

    const passengerType = props.placeholderData.key.slice(0, 3);
    const baseLocalePath = `passenger.types.${passengerType}`;
    const maxAge = props.placeholderData.ageBetween.max
      ? props.placeholderData.ageBetween.max + 1 // +1 since max age should not be included in interval
      : props.placeholderData.ageBetween.max;

    return {
      title: t(`${baseLocalePath}.text`),
      description: t(`${baseLocalePath}.description`, {
        min: props.placeholderData.ageBetween.min,
        max: maxAge,
      }),
    };
  });

  const hasSelectedPassenger = computed(() => {
    return Boolean(props.passenger?.firstName);
  });

  const passengerText = computed(() => {
    if (!hasSelectedPassenger.value) {
      return passengerTypePlaceholder.value;
    }

    const { lastName, firstName, document } = props.passenger;
    const documentType = (document as { type?: string }).type || 'P'; // We can write any value to default text to be displayed

    return {
      title: `${lastName} ${firstName}`,
      description: bookingPassengerLib.getPaxDocText(
        documentType,
        document.number,
      ),
    };
  });
</script>

<style scoped>
  .passenger-action-button {
    --free-btn-border-radius: 0.75rem;
  }
</style>
